




























































import { computed, defineComponent, onBeforeMount, ref } from '@vue/composition-api';
import axios from 'axios';

import { APP_NAME, SERVER_URL } from '@/environment';
import store from '@/store';
import { logout } from '@/utils/auth';

export default defineComponent({
  name: 'EmailVerification',
  setup(props, ctx) {
    const sending = ref(false);
    const message = ref('');
    const messageType = ref<'success' | 'error'>('success');
    const queryEmail = typeof ctx.root.$route.query.email === 'string'
      ? ctx.root.$route.query.email
      : '';
    const changeEmailRequested = ctx.root.$route.query.changeEmail === '1';
    const verificationSent = ref(ctx.root.$route.query.sent === '1');
    const signedIn = computed(() => store.state.client !== null);
    const pendingEmailChangeToken = window.sessionStorage.getItem('painfacePendingEmailChangeToken') || '';
    const canChangeEmail = computed(() => signedIn.value || !!pendingEmailChangeToken);
    const email = computed(() => store.state.client?.user?.email || queryEmail);
    const emailToVerify = ref(email.value);
    const emailInput = ref(emailToVerify.value);
    const emailError = ref('');
    const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
    const hasValidEmail = computed(() => validEmail(emailToVerify.value));
    const changeEmailDialog = ref(false);
    const changingEmail = ref(false);

    onBeforeMount(() => {
      if (!store.state.client && !validEmail(queryEmail)) {
        ctx.root.$router.replace('/login');
      } else if (store.state.client?.user?.email_verified && !changeEmailRequested) {
        ctx.root.$router.replace('/app');
      }
      if (changeEmailRequested && store.state.client) {
        changeEmailDialog.value = true;
      }
    });

    async function send() {
      if (!hasValidEmail.value) {
        openChangeEmail();
        return;
      }
      sending.value = true;
      message.value = '';
      try {
        if (store.state.client) {
          message.value = await store.state.client.sendEmailVerification(emailToVerify.value.trim().toLowerCase());
        } else {
          const response = await axios.post(`${SERVER_URL}/auth/email-verification/request`, {
            email: emailToVerify.value.trim().toLowerCase(),
          });
          message.value = response.data;
        }
        messageType.value = 'success';
        verificationSent.value = true;
      } catch (error) {
        const response = (error as { response?: { data?: string } }).response;
        message.value = response?.data || 'Unable to send verification email; please try again later.';
        messageType.value = 'error';
      } finally {
        sending.value = false;
      }
    }

    function openChangeEmail() {
      emailInput.value = emailToVerify.value;
      emailError.value = '';
      changeEmailDialog.value = true;
    }

    async function applyEmailChange() {
      if (!validEmail(emailInput.value)) {
        emailError.value = 'Enter a valid email address';
        return;
      }
      const changedEmail = emailInput.value.trim().toLowerCase();
      changingEmail.value = true;
      emailError.value = '';
      try {
        if (store.state.client) {
          // The address is persisted when Send verification email is pressed.
          emailToVerify.value = changedEmail;
        } else {
          const response = await axios.post(`${SERVER_URL}/auth/email-verification/change-pending`, {
            token: pendingEmailChangeToken,
            email: changedEmail,
          });
          emailToVerify.value = changedEmail;
          message.value = response.data;
          messageType.value = 'success';
          verificationSent.value = false;
        }
        changeEmailDialog.value = false;
        message.value = store.state.client ? '' : message.value;
      } catch (error) {
        const response = (error as { response?: { data?: string } }).response;
        emailError.value = response?.data || 'Unable to update email; please try again later.';
      } finally {
        changingEmail.value = false;
      }
    }

    async function signOut() {
      await logout();
      ctx.root.$router.replace('/login');
    }

    return {
      APP_NAME,
      email,
      emailError,
      emailInput,
      emailToVerify,
      changeEmailDialog,
      canChangeEmail,
      changingEmail,
      hasValidEmail,
      message,
      messageType,
      openChangeEmail,
      applyEmailChange,
      send,
      sending,
      signedIn,
      signOut,
      verificationSent,
    };
  },
});
